import React, { FC, useCallback, useRef, useState } from 'react';
import { Alert, Button, Modal, Row, Spin, Tooltip, message } from 'antd';
import {
	CloudDownloadOutlined,
	CloudUploadOutlined,
	EditOutlined,
	FullscreenExitOutlined,
	FullscreenOutlined,
	PlusCircleFilled,
	WarningOutlined,
} from '@ant-design/icons';
import {
	ImportField,
	ImportFieldEnumOption,
	ImportRow,
	ColumnMapping,
	FieldValueMapping,
	FieldValueMappingEntry,
} from './ImportGridTypes';
import { parseCsvFile } from './csvParser';
import ColumnMapper from './ColumnMapper';
import FieldValueMapper from './FieldValueMapper';
import EditableImportGrid from './EditableImportGrid';
import { isRowFullyEmpty, validateRows } from './validateRows';
import useMountEffect from '../../hooks/useMountEffect';
import ButtonWithTimer from '../common/ButtonWithTimer';
import { WARNING_BUTTON_WAIT_TIME } from '../../utils/DataConstants';

type Step = 'sample' | 'upload' | 'map' | 'fieldValues' | 'edit';

interface ImportGridModalProps {
	visible: boolean;
	title?: string;
	fields: ImportField[];
	initialData?: Record<string, any>[];
	skipUpload?: boolean;
	recordHook?: (record: any) => any;
	validateRecordsOnSubmit?: (data: any[]) => string | undefined | null | false;
	onDataLoad: (result: { validData: any[] }) => Promise<any>;
	onCancel: () => void;
	onComplete?: () => void;
	// Sample-step config. When `onDownloadSample` is provided, the modal opens
	// on a 'sample' step that offers the legacy download + upload + manual
	// entry choices in a single screen; without it, the modal starts on the
	// upload step (the two-box choice). Mirrors the props previously held by
	// the wrapper `OWImportButton`'s pre-modal.
	onDownloadSample?: () => void;
	// Spinner on the Download Sample button while the consumer builds the file.
	downloadingSample?: boolean;
	sampleModalText?: string;
	sampleButtonText?: string;
	showWarningMessage?: boolean;
	entityName?: string;
	// Whether the user can add rows in the edit grid. Defaults to true; set false for a fixed row
	// set (e.g. editing a value per existing record).
	allowAddRows?: boolean;
	// Whether the user can delete rows in the edit grid. Defaults to true; set false for a fixed
	// row set where removing a record makes no sense in context.
	allowDeleteRows?: boolean;
	// Optional async pass over the rows the moment they first reach the grid — after column mapping,
	// after enum-value mapping, on manual entry, and on the skipUpload/initialData path. Lets a
	// consumer resolve rows against the server and return enriched values (e.g. filling a readonly
	// display column with a looked-up name) BEFORE the user is asked to submit, so unmatched rows
	// surface immediately instead of at submit time.
	//
	// Errors are not returned from here. The consumer stashes whatever failed and reports it from its
	// `recordHook`, which this runs afterwards — the same mechanism the submit path already uses, so
	// there is one way for per-row errors to reach the grid rather than two.
	//
	// A rejection is swallowed and the un-enriched rows are shown, on the principle that a failed
	// pre-check should not block the user from reaching the grid; submit still validates.
	resolveRows?: (rows: Record<string, any>[]) => Promise<Record<string, any>[]>;
	// Message shown beside the spinner while `resolveRows` is in flight.
	resolvingRowsText?: string;
}

const buildMappedData = (
	csvData: Record<string, string>[],
	mappings: ColumnMapping[],
	fields: ImportField[]
): Record<string, any>[] =>
	csvData.map((csvRow) => {
		const row: Record<string, any> = {};
		mappings.forEach((mapping) => {
			if (mapping.fieldKey) {
				row[mapping.fieldKey] =
					csvRow[mapping.csvHeader] !== undefined ? csvRow[mapping.csvHeader] : '';
			}
		});
		// Trim strings
		fields.forEach((field) => {
			if (typeof row[field.key] === 'string') {
				row[field.key] = row[field.key].trim();
			}
		});
		// Booleans
		fields.forEach((field) => {
			if (field.type === 'boolean' && row[field.key] !== undefined && row[field.key] !== '') {
				const raw = String(row[field.key]).trim().toLowerCase();
				row[field.key] = raw === 'true' || raw === '1' || raw === 'yes';
			}
		});
		// Numbers
		fields.forEach((field) => {
			if (field.type === 'number' && row[field.key] !== undefined && row[field.key] !== '') {
				const parsed = Number(row[field.key]);
				if (!isNaN(parsed)) {
					row[field.key] = parsed;
				}
			}
		});
		return row;
	});

// Does this option accept `loweredValue` as a spelling of itself? Matches the
// display label, the underlying value, and any `alternativeNames` the consumer
// declared. Both sides are trimmed: exported reports routinely carry trailing
// spaces in names (e.g. "Central Club ") that the CSV's own value has already
// had trimmed off by buildMappedData.
const optionMatchesValue = (option: ImportFieldEnumOption, loweredValue: string): boolean => {
	const candidates: (string | number)[] = [
		option.label,
		option.value,
		...(option.alternativeNames || []),
	];
	return candidates.some(
		(candidate) => String(candidate).trim().toLowerCase() === loweredValue.trim()
	);
};

const computeFieldValueMappings = (
	data: Record<string, any>[],
	fields: ImportField[]
): FieldValueMapping[] => {
	const enumFields = fields.filter(
		(field) =>
			field.type === 'enum' &&
			field.config &&
			field.config.options &&
			field.config.options.length > 0
	);

	const result: FieldValueMapping[] = [];

	enumFields.forEach((field) => {
		const valueCounts: Record<string, number> = {};
		data.forEach((row) => {
			const val = row[field.key];
			if (val !== undefined && val !== null && val !== '') {
				const strVal = String(val).trim();
				if (strVal) {
					valueCounts[strVal] = (valueCounts[strVal] || 0) + 1;
				}
			}
		});

		const entries: FieldValueMappingEntry[] = [];

		Object.keys(valueCounts).forEach((csvValue) => {
			const rawVal = csvValue.toLowerCase();
			const matched = field.config!.options.find((option) => optionMatchesValue(option, rawVal));

			if (matched) {
				entries.push({
					csvValue: csvValue,
					mappedValue: matched.value,
					autoMatched: true,
					count: valueCounts[csvValue],
				});
			} else {
				entries.push({
					csvValue: csvValue,
					mappedValue: null,
					autoMatched: false,
					count: valueCounts[csvValue],
				});
			}
		});

		if (entries.length > 0) {
			result.push({
				fieldKey: field.key,
				fieldLabel: field.label,
				options: field.config!.options,
				entries: entries,
			});
		}
	});

	return result;
};

const applyEnumMappings = (
	data: Record<string, any>[],
	fields: ImportField[],
	userMappings: FieldValueMapping[]
): Record<string, any>[] => {
	// Build lookup: fieldKey -> { csvValue (lowered) -> enumOptionValue }
	const lookups: Record<string, Record<string, string | number>> = {};
	userMappings.forEach((fieldMapping) => {
		const map: Record<string, string | number> = {};
		fieldMapping.entries.forEach((entry) => {
			if (entry.mappedValue !== null) {
				map[entry.csvValue.toLowerCase()] = entry.mappedValue;
			}
		});
		lookups[fieldMapping.fieldKey] = map;
	});

	return data.map((row) => {
		const newRow = Object.assign({}, row);
		fields.forEach((field) => {
			if (field.type === 'enum' && field.config && field.config.options && newRow[field.key]) {
				const rawVal = String(newRow[field.key]).trim();
				const rawValLower = rawVal.toLowerCase();

				// Check user mappings first
				if (lookups[field.key] && lookups[field.key][rawValLower] !== undefined) {
					newRow[field.key] = lookups[field.key][rawValLower];
				} else {
					// Fall back to auto-matching
					const matched = field.config.options.find((option) =>
						optionMatchesValue(option, rawValLower)
					);
					if (matched) {
						newRow[field.key] = matched.value;
					}
				}
			}
		});
		return newRow;
	});
};

// An untouched cell must be left OUT of the payload, not sent as '' or null.
// The bulk endpoints read each field with DataValidator's optValue, which only
// treats a *missing* key as absent: JsonUtil maps JSON null to Scala's `None`
// object, so an explicit null arrives as Some(None) and slips past that guard.
// optValue's `asInstanceOf[T]` is erased and therefore never throws, so the None
// escapes typed as the target — and optValueInt's `.map(_.toLong)`, which sits
// outside the Try, then dies with a ClassCastException and a 500. '' fails the
// same way for numeric and id fields. Omitting the key is the only shape that
// maps cleanly onto None.
//
// The grid keeps '' internally so its inputs stay controlled; this is the submit
// boundary, and the payload the consumer's onDataLoad receives.
const extractCleanData = (rows: ImportRow[]): Record<string, any>[] =>
	rows.map((row) => {
		const clean: Record<string, any> = {};
		Object.keys(row).forEach((key) => {
			if (key === '_rowId' || key === '_errors') return;
			if (row[key] === '' || row[key] === undefined || row[key] === null) return;
			clean[key] = row[key];
		});
		return clean;
	});

const ImportGridModal: FC<ImportGridModalProps> = ({
	visible,
	title,
	fields,
	initialData,
	skipUpload,
	recordHook,
	validateRecordsOnSubmit,
	onDataLoad,
	onCancel,
	onComplete,
	onDownloadSample,
	downloadingSample,
	sampleModalText,
	sampleButtonText,
	showWarningMessage,
	entityName,
	allowAddRows = true,
	allowDeleteRows = true,
	resolveRows,
	resolvingRowsText,
}) => {
	const initialStep: Step =
		skipUpload && initialData ? 'edit' : onDownloadSample ? 'sample' : 'upload';
	const [step, setStep] = useState<Step>(initialStep);
	const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
	const [csvData, setCsvData] = useState<Record<string, string>[]>([]);
	const [rows, setRows] = useState<ImportRow[]>([]);
	const [mappedData, setMappedData] = useState<Record<string, any>[]>([]);
	const [fieldValueMappings, setFieldValueMappings] = useState<FieldValueMapping[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const [resolvingRows, setResolvingRows] = useState(false);
	// Tracks whether the user has touched the data (edited/added/deleted grid rows
	// or uploaded a file). Drives the "discard changes?" confirmation on close —
	// loading initial data or navigating import steps does not flip this, only
	// genuine user input does. Reset on a successful submit.
	const [dirty, setDirty] = useState(false);
	// Whether the edit grid has been expanded to fill the viewport. Opt-in — the modal
	// opens at its normal windowed size — and sticky for the rest of the session, so a
	// user who works full screen doesn't have to re-expand on every import.
	const [fullScreen, setFullScreen] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const rowIdRef = useRef(1);

	const nextRowId = () => rowIdRef.current++;

	const buildEmptyImportRow = (): ImportRow => {
		const row: ImportRow = { _rowId: nextRowId(), _errors: {} };
		fields.forEach((field) => {
			row[field.key] = field.type === 'boolean' ? false : '';
		});
		return row;
	};

	const toImportRows = (data: Record<string, any>[]): ImportRow[] =>
		data.map((item) => {
			const row: ImportRow = { _rowId: nextRowId(), _errors: {} };
			fields.forEach((field) => {
				row[field.key] = item[field.key] !== undefined ? item[field.key] : '';
			});
			return row;
		});

	const runInitialValidation = useCallback(
		(importRows: ImportRow[]): ImportRow[] => validateRows(importRows, fields, recordHook),
		[fields, recordHook]
	);

	// Single entry point to the edit step, so the optional `resolveRows` pre-check cannot be
	// forgotten on one of the four paths that reach the grid.
	const enterEditStep = useCallback(
		async (data: Record<string, any>[]) => {
			if (!resolveRows) {
				setRows(runInitialValidation(toImportRows(data)));
				setStep('edit');
				return;
			}
			setResolvingRows(true);
			let resolved = data;
			try {
				resolved = (await resolveRows(data)) || data;
			} catch (err) {
				// Deliberately non-fatal — see the prop's comment. Submit still validates.
				resolved = data;
			}
			setRows(runInitialValidation(toImportRows(resolved)));
			setStep('edit');
			setResolvingRows(false);
		},
		[resolveRows, runInitialValidation] // eslint-disable-line react-hooks/exhaustive-deps
	);

	// Run initial validation for skipUpload + initialData path
	useMountEffect(() => {
		if (skipUpload && initialData && rows.length === 0) {
			enterEditStep(initialData);
		}
	});

	const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files && e.target.files[0];
		if (!file) return;
		try {
			const result = await parseCsvFile(file);
			if (result.headers.length === 0) {
				message.error('CSV file appears to be empty or has no headers.');
				return;
			}
			setCsvHeaders(result.headers);
			setCsvData(result.data);
			setDirty(true);
			setStep('map');
		} catch (err) {
			message.error(`Failed to parse CSV: ${err}`);
		}
	}, []);

	const handleMappingComplete = useCallback(
		(mappings: ColumnMapping[]) => {
			const data = buildMappedData(csvData, mappings, fields);
			const fvMappings = computeFieldValueMappings(data, fields);

			setMappedData(data);

			const hasAnyUnmatched = fvMappings.some((fm) =>
				fm.entries.some((entry) => !entry.autoMatched)
			);

			if (hasAnyUnmatched) {
				setFieldValueMappings(fvMappings);
				setStep('fieldValues');
			} else {
				// Every enum value auto-matched (or no enum values present) — skip the
				// confirmation step but still resolve labels to option values via
				// applyEnumMappings' fallback path.
				const resolved = applyEnumMappings(data, fields, []);
				enterEditStep(resolved);
			}
		},
		[csvData, fields, enterEditStep] // eslint-disable-line react-hooks/exhaustive-deps
	);

	const handleFieldValueMappingComplete = useCallback(
		(confirmedMappings: FieldValueMapping[]) => {
			const resolved = applyEnumMappings(mappedData, fields, confirmedMappings);
			enterEditStep(resolved);
		},
		[mappedData, fields, enterEditStep] // eslint-disable-line react-hooks/exhaustive-deps
	);

	const handleSubmit = useCallback(async () => {
		// Drop any fully-empty rows (e.g. an unused "Add Row" / the initial manual
		// row left blank) so they never reach the backend.
		const submittableRows = rows.filter((row) => !isRowFullyEmpty(row, fields));

		const hasFieldErrors = submittableRows.some((row) =>
			Object.values(row._errors || {}).some((errs) => errs.length > 0)
		);

		const validData = extractCleanData(submittableRows);

		let batchError: string | undefined | null | false = null;
		if (validateRecordsOnSubmit) {
			batchError = validateRecordsOnSubmit(validData);
		}

		if (hasFieldErrors || batchError) {
			if (hasFieldErrors) message.error('Fix validation errors before submitting.');
			if (batchError) message.error(batchError);
			return;
		}

		setSubmitting(true);
		try {
			const result = await onDataLoad({ validData });
			message.success(result || 'Data submitted successfully!');
			setDirty(false);
			if (onComplete) onComplete();
		} catch (err) {
			// Re-run validation so the consumer's recordHook can surface anything
			// it collected during submit (e.g., backend per-row errors stored in
			// a ref). Constraint and recordHook errors land on rows via the
			// standard `_errors` mechanism.
			setRows((current) => validateRows(current, fields, recordHook));
			// A falsy rejection signals a silent cancel (e.g. the consumer showed a confirm dialog
			// and the user dismissed it). Keep the grid open without surfacing an error toast.
			if (err) {
				message.error(typeof err === 'string' ? err : 'Submission failed.');
			}
		} finally {
			setSubmitting(false);
		}
	}, [rows, validateRecordsOnSubmit, onDataLoad, onComplete, fields, recordHook]);

	const handleRowsChange = useCallback((updatedRows: ImportRow[]) => {
		setRows(updatedRows);
		setDirty(true);
	}, []);

	// Bumped on every settle. A resolve that finishes after the user has already moved on is stale
	// and must not overwrite what they typed since, so its result is discarded.
	const settleTokenRef = useRef(0);

	/**
	 * An edit settled (focus left the cell, or Enter). Re-runs the consumer's `resolveRows` so
	 * looked-up display columns refresh and per-row errors are re-evaluated against the new value —
	 * the same pass that runs after mapping, just incremental. No spinner: this fires during normal
	 * typing and blanking the grid each time would be unusable.
	 */
	const handleRowsSettled = useCallback(
		(settledRows: ImportRow[]) => {
			if (!resolveRows) return;
			const token = settleTokenRef.current + 1;
			settleTokenRef.current = token;

			// Snapshot what we hand the resolver, alongside the `_rowId` of the row each entry came
			// from. `resolveRows` takes cleaned data (no `_rowId`) and answers positionally, so these
			// two are the only way back to row identity once the promise settles.
			const requestedRows = extractCleanData(settledRows);
			const requestedRowIds = settledRows.map((row) => row._rowId);

			Promise.resolve(resolveRows(requestedRows))
				.then((enrichedData) => {
					if (!enrichedData || settleTokenRef.current !== token) return;

					// Merging by POSITION would be wrong: adding, inserting or deleting a row while the
					// lookup is in flight goes through `handleRowsChange`, which does not bump the settle
					// token, so `current` can be a different list by now — deleting the first of [A, B]
					// would splat A's values (Asset ID included) onto the surviving B. Key the answer to
					// the row it was computed from instead, and keep only the fields the resolver itself
					// changed, so a late arrival can never overwrite a source value the user has edited
					// since. Rows added after the request simply match nothing and are left alone.
					const resolvedFieldsByRowId = new Map<number, Record<string, any>>();
					requestedRowIds.forEach((rowId, index) => {
						const enrichedRow = enrichedData[index];
						if (!enrichedRow) return;
						const requestedRow = requestedRows[index] || {};
						const resolverOwnedFields: Record<string, any> = {};
						Object.keys(enrichedRow).forEach((key) => {
							if (key === '_rowId' || key === '_errors') return;
							if (enrichedRow[key] !== requestedRow[key]) {
								resolverOwnedFields[key] = enrichedRow[key];
							}
						});
						resolvedFieldsByRowId.set(rowId, resolverOwnedFields);
					});

					setRows((current) =>
						validateRows(
							current.map((row) => ({
								...row,
								...(resolvedFieldsByRowId.get(row._rowId) || {}),
							})),
							fields,
							recordHook
						)
					);
				})
				.catch(() => {
					// A failed incremental lookup leaves the previous state in place; submit re-checks.
				});
		},
		[resolveRows, fields, recordHook]
	);

	// Intercept every close path (Cancel button, Escape, the X, mask click). When
	// the user has unsaved changes, confirm before discarding; otherwise close
	// straight through.
	const handleRequestClose = useCallback(() => {
		if (!dirty) {
			onCancel();
			return;
		}
		Modal.confirm({
			title: 'Discard changes?',
			content: 'You have unsaved changes. If you close now, your changes will be lost.',
			okText: 'Discard changes',
			okType: 'danger',
			cancelText: 'Keep editing',
			onOk: onCancel,
		});
	}, [dirty, onCancel]);

	const handleManualEntry = useCallback(() => {
		// Manual entry starts with a single blank row to type into; the user adds
		// more via "Add Row" (which now toasts and scrolls the new row into view).
		// No resolve pre-check here: a single blank row has nothing to look up yet.
		setRows(runInitialValidation([buildEmptyImportRow()]));
		setStep('edit');
	}, [fields, runInitialValidation]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleUploadAreaClick = useCallback(() => {
		if (fileInputRef.current) fileInputRef.current.click();
	}, []);

	const handleProceedToUpload = useCallback(() => {
		// Skip the upload-step UI entirely — open the native file picker
		// straight away. If the user cancels, they stay on the sample step and
		// can hit Proceed again.
		if (fileInputRef.current) fileInputRef.current.click();
	}, []);

	const handleToggleFullScreen = useCallback(
		() => setFullScreen((currentlyFullScreen) => !currentlyFullScreen),
		[]
	);

	// The edit step is a spreadsheet, so it opens wider and taller than the form steps
	// and can be expanded further to fill the viewport. Full screen is opt-in via the
	// toggle in the title bar; the default is the ordinary centred dialog.
	const isEditStep = step === 'edit';
	const isFullScreen = isEditStep && fullScreen;
	const modalWidth = isFullScreen
		? '100vw'
		: isEditStep
		? '90vw'
		: step === 'fieldValues'
		? 700
		: 600;

	// Both edit-step layouts pin the body to a definite height and make it a flex column,
	// so EditableImportGrid can stretch to fill it (h-full / flex-1) and scroll
	// internally rather than growing the dialog. Windowed mode uses a fixed 90vh minus
	// the title + footer chrome; full screen hands that job to flexbox in the <style>
	// block below, which is what lets the compact chrome there translate directly into
	// more visible rows. Other steps keep antd's default content-sized body.
	const editBodyStyle: React.CSSProperties = {
		height: isFullScreen ? undefined : 'calc(90vh - 108px)',
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	};

	const fullScreenLabel = fullScreen ? 'Exit full screen' : 'Full screen';

	return (
		<>
			{isEditStep ? (
				<style>{`
					/* Three page-level .less files (BuyerAdmin{,Invoice,Quote}ApprovalPage)
					   declare an UNSCOPED ".ant-modal-body { max-height: 600px;
					   overflow-y: scroll }", and src/index.less adds ".ant-modal-body {
					   overflow: auto !important }". Both leak to every modal in the app, so
					   without this the grid body is capped at 600px no matter how tall the
					   dialog is — which is why full screen gained width but no rows. Undoing
					   them here rather than there keeps the blast radius to this modal;
					   the !important is needed to outrank index.less's own. */
					.ant-modal.import-grid-modal .ant-modal-body {
						max-height: none;
						overflow: hidden !important;
					}
					.ant-modal.import-grid-fullscreen-modal {
						top: 0;
						margin: 0;
						max-width: 100vw;
						padding-bottom: 0;
					}
					.ant-modal.import-grid-fullscreen-modal .ant-modal-content {
						display: flex;
						flex-direction: column;
						height: 100vh;
						border-radius: 0;
					}
					/* Trim the chrome so the height goes to the grid instead: every pixel
					   saved on the title bar, the body padding and the footer is another
					   pixel of visible rows. Roughly 60px reclaimed vs antd's defaults
					   (24px header, 24px body padding, 12px footer). */
					.ant-modal.import-grid-fullscreen-modal .ant-modal-header {
						padding: 10px 16px;
					}
					/* The close X is sized for antd's 56px-tall header, so it would hang
					   below the compact one and sit off-centre. */
					.ant-modal.import-grid-fullscreen-modal .ant-modal-close-x {
						width: 44px;
						height: 44px;
						line-height: 44px;
					}
					/* min-height: 0 lets the body shrink below its content's intrinsic
					   height, which is what allows the grid inside to scroll instead of
					   pushing the footer off-screen. */
					.ant-modal.import-grid-fullscreen-modal .ant-modal-body {
						flex: 1;
						min-height: 0;
						padding: 12px 16px;
					}
					.ant-modal.import-grid-fullscreen-modal .ant-modal-footer {
						padding: 8px 16px;
					}
				`}</style>
			) : null}
			<Modal
				visible={visible}
				title={
					isEditStep ? (
						<div className="flex items-center gap-2">
							<span>{title || 'Import Data'}</span>
							<Tooltip title={fullScreenLabel}>
								<Button
									type="text"
									size="small"
									aria-label={fullScreenLabel}
									icon={
										fullScreen ? (
											<FullscreenExitOutlined translate="" />
										) : (
											<FullscreenOutlined translate="" />
										)
									}
									onClick={handleToggleFullScreen}
								/>
							</Tooltip>
						</div>
					) : (
						title || 'Import Data'
					)
				}
				width={modalWidth}
				centered={isEditStep && !isFullScreen}
				className={
					isFullScreen
						? 'import-grid-modal import-grid-fullscreen-modal'
						: isEditStep
						? 'import-grid-modal'
						: undefined
				}
				bodyStyle={isEditStep ? editBodyStyle : undefined}
				onCancel={handleRequestClose}
				footer={
					isEditStep ? (
						<>
							<Button onClick={handleRequestClose}>Cancel</Button>
							<Button type="primary" onClick={handleSubmit} loading={submitting}>
								Submit
							</Button>
						</>
					) : null
				}
				destroyOnClose
			>
				{resolvingRows ? (
					<div className="flex flex-col items-center justify-center py-12">
						<Spin />
						<div className="bodyregular mt-4 text-gray-400">
							{resolvingRowsText || 'Checking your data...'}
						</div>
					</div>
				) : null}
				{!resolvingRows && step === 'sample' && (
					<>
						<Row>{sampleModalText || 'Do you want to download a sample file?'}</Row>
						{showWarningMessage ? (
							<>
								<div className="mt-2">
									<Alert
										message="You will lose data if you do not read this carefully."
										type="error"
									/>
								</div>
								<div className="mt-4 flex flex-row items-center">
									<WarningOutlined translate="" />
									<div className="ml-2 text-gray-400">
										{`Please ensure that you always upload the entire ${
											entityName || 'data'
										} list. You can download the list here to make updates, addition/deletion.`}
									</div>
								</div>
							</>
						) : null}
						<Row>
							<Button
								type="primary"
								className="mt-6 w-full"
								loading={downloadingSample}
								onClick={onDownloadSample}
								icon={<CloudDownloadOutlined translate="" />}
							>
								{sampleButtonText || 'Download Sample File'}
							</Button>
						</Row>
						<Row>
							{showWarningMessage ? (
								<ButtonWithTimer
									icon={<PlusCircleFilled translate="" />}
									className="mt-3 w-full"
									disableUntilSeconds={WARNING_BUTTON_WAIT_TIME}
									onClick={handleProceedToUpload}
								>
									Proceed to Upload
								</ButtonWithTimer>
							) : (
								<Button
									icon={<PlusCircleFilled translate="" />}
									className="mt-3 w-full"
									onClick={handleProceedToUpload}
								>
									Proceed to Upload
								</Button>
							)}
						</Row>
						<Row>
							<Button
								icon={<EditOutlined translate="" />}
								className="mt-3 w-full"
								onClick={handleManualEntry}
							>
								Enter Manually
							</Button>
						</Row>
					</>
				)}

				{/*
				 * File input lives outside the upload step so handleProceedToUpload
				 * (which fires from the sample step) can invoke .click() without
				 * waiting for the upload step to mount.
				 */}
				<input
					ref={fileInputRef}
					type="file"
					accept=".csv"
					className="hidden"
					onChange={handleFileSelect}
				/>

				{!resolvingRows && step === 'upload' && (
					<div className="flex gap-4">
						<div
							className="flex-1 cursor-pointer rounded-lg border-2 border-dashed border-owgray px-5 py-10 text-center"
							onClick={handleUploadAreaClick}
						>
							<CloudUploadOutlined className="text-5xl text-[#999]" />
							<div className="mt-3 text-[#666]">Upload CSV</div>
						</div>
						{!onDownloadSample && (
							<div
								className="flex-1 cursor-pointer rounded-lg border-2 border-dashed border-owgray px-5 py-10 text-center"
								onClick={handleManualEntry}
							>
								<EditOutlined className="text-5xl text-[#999]" />
								<div className="mt-3 text-[#666]">Enter Manually</div>
							</div>
						)}
					</div>
				)}

				{!resolvingRows && step === 'map' && (
					<ColumnMapper
						csvHeaders={csvHeaders}
						fields={fields}
						onMappingComplete={handleMappingComplete}
					/>
				)}

				{!resolvingRows && step === 'fieldValues' && (
					<FieldValueMapper
						fieldValueMappings={fieldValueMappings}
						onConfirm={handleFieldValueMappingComplete}
					/>
				)}

				{!resolvingRows && isEditStep && (
					<EditableImportGrid
						fields={fields}
						rows={rows}
						onRowsChange={handleRowsChange}
						onRowsSettled={handleRowsSettled}
						recordHook={recordHook}
						allowAddRows={allowAddRows}
						allowDeleteRows={allowDeleteRows}
					/>
				)}
			</Modal>
		</>
	);
};

export default ImportGridModal;
